import { parseGoogleAddressComponents, googlePlaceToPoi } from "@/components/organisms/MyFavourites/FavouriteLocationsTable/suggestions";
import { FlightTerminalLeg, flightTerminalLabel, terminalFromText } from "@/helpers/bookingHelpers";
import { getPlacePredictionsForCountries } from "@/lib/googlePlaces";
import { snapToNearestRoad } from "@/lib/googleRoads";
import { PointsOfInterest, RM_TAGS } from "@/redux/apis/clientPoi/types";

/** `Terminal 3` → `3`. The designator, for building the query and the label. */
const terminalToken = (label: string) => label.replace(/^Terminal\s+/i, "");

/**
 * The flight leg's terminal as a Google-resolved POI — the fallback for an operator whose provider
 * POIs carry no row for it (`findProviderTerminalPoi` first, always).
 *
 * COUNTRY-SCOPED ON BOTH LEGS (DEV-1606 × DEV-1487), and that is safe rather than merely convenient.
 * The terminal being resolved always belongs to an airport that is ALREADY a stop on this booking —
 * the pickup for an arrival, the drop-off for a departure — so if the client was allowed to type that
 * address, its country is in this list by construction. An INTERNATIONAL departure is no exception:
 * "international" describes where the flight is going, while the terminal we resolve is the one it
 * leaves FROM, and the lookup refuses a leg departing anywhere but the drop-off airport the client
 * typed (contract §1). So a client restricted to AU still resolves SYD T1 for a Sydney → Manila
 * flight; what they cannot resolve is a terminal in a country they could not have booked in anyway.
 * The one exception is `null` — an operator booking on behalf under the Worldwide Address Search add-on
 * (DEV-1612) — where the lookup runs unrestricted, exactly as their search box does.
 *
 * Reads `window.google.maps.places` rather than taking the services as arguments, exactly as
 * `LocationPanel`'s geocode does, so nothing here loads Google: the caller holds the single
 * `useLoadScript` the Map atom and `useAddressSearch` share, and only calls in once it reports loaded.
 * That also makes this a plain async function a test can drive against a stubbed namespace.
 *
 * The resulting POI is relabelled rather than left as Google returned it. `googlePlaceToPoi` yields an
 * empty `rm_label` and no tags (a Google prediction has an address, not a name), but the sheet reads the
 * terminal and the IATA back OUT of the label — `extractTerminal` / `extractAirportIata` drive the
 * left-panel terminal row, the leg picker on multi-leg flights, and the idempotence check that stops this
 * swap running twice. An unlabelled airport POI would break all three.
 */
export const findGoogleTerminalPoi = async (
  leg: FlightTerminalLeg,
  /** The countries the sheet may book in (`useAddressSearchCountries`); empty = no lookup, `null` = worldwide. */
  countries: string[] | null
): Promise<PointsOfInterest | null> => {
  const label = flightTerminalLabel(leg.terminal);
  const places = window.google?.maps?.places;
  if (!label || !leg.iata || !leg.name || !places || (countries !== null && countries.length === 0)) return null;

  const token = terminalToken(label);
  // Wrapped whole rather than per call: the two `new places.*Service()` constructors run inside promise
  // executors, so a Places namespace that is present but broken would reject out of the caller's effect
  // as an unhandled rejection. A failed lookup is a MISS — the pickup simply stays where the client put
  // it — which is the same graceful degradation `snapToNearestRoad` gives the rest of the address flow.
  try {
    // One request per five countries, merged — Google's per-request limit must not drop a country (DEV-1612).
    const predictions = await getPlacePredictionsForCountries(
      new places.AutocompleteService(),
      { input: `${leg.name} Terminal ${token}` },
      countries
    );

    // Google happily answers "Sydney Airport Terminal 3" with the airport itself, which would swap the
    // pickup to a POI carrying no terminal at all — and then re-run for ever, since the pickup would still
    // not match the flight. Only a prediction that names THIS terminal is the terminal.
    const match = predictions.find((prediction) => terminalFromText(prediction.description) === label);
    if (!match) return null;

    const details = await new Promise<google.maps.places.PlaceResult | null>((resolve) => {
      new places.PlacesService(document.createElement("div")).getDetails(
        { placeId: match.place_id, fields: ["geometry.location", "address_components"] },
        (result) => resolve(result)
      );
    });

    const location = details?.geometry?.location;
    if (!location) return null;
    // Snap to the nearest road before committing it, as every other picked address is (mobile parity).
    const snapped = await snapToNearestRoad({ lat: location.lat(), lng: location.lng() });
    const poi = googlePlaceToPoi(
      { description: match.description, place_id: match.place_id, types: match.types },
      snapped,
      parseGoogleAddressComponents(details?.address_components ?? [])
    );
    return {
      ...poi,
      rm_label: `${leg.name} Terminal T${token} (${leg.iata})`,
      rm_tags: [RM_TAGS.Airport, RM_TAGS.FixedPin],
    };
  } catch {
    return null;
  }
};
